// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import './ReportingDisaggregatedSelectorMultiLine.scss';

import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Selector, usePrevious } from 'sonata-react-components';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';
import { chartDisaggregatedVariables } from 'sonata-static-storage/lib/samples/statsPreferences.samples';

import {
  campaignsHaveContextualBidding,
  campaignsHaveDCO,
  getVariableFilterPermission,
  variableNameFormatter
} from '@/helpers/reporting.helpers';
import { getSelectedCampaigns } from '@/store/selectors/reporting.selectors';
import { getCampaigns } from '@/store/selectors/reportingAdvancedFilter.selectors';
import { getUserRoles } from '@/store/selectors/resources.selectors';
import { getCurrentUser } from '@/store/selectors/user.selectors';

function ReportingDisaggregatedSelectorMultiLine({ hideKpis, metricsKpis, onChangeDisaggregatedOption }) {
  const { formatMessage } = useIntl();
  const user = useSelector(getCurrentUser);
  const userRoles = useSelector(getUserRoles);
  const reportingCampaignsIds = useSelector(getCampaigns);
  const selectedCampaign = useSelector(getSelectedCampaigns);
  const [{ by, metric }, setState] = useState({
    by: null,
    metric: 'none'
  });

  const prevMetrics = usePrevious(metricsKpis) || null;

  useEffect(() => {
    if (
      !prevMetrics ||
      !metricsKpis ||
      prevMetrics.kpi.id !== metricsKpis.kpi.id ||
      prevMetrics.metric.id !== metricsKpis.metric.id
    ) {
      setState(prevState => ({
        ...prevState,
        by: null,
        metric: 'none'
      }));
      onChangeDisaggregatedOption('none', null);
    }
  }, [metricsKpis]);

  const isDisabledDisaggregatedBySelector = useMemo(() => metric === 'none', [metric]);

  const selectorOptions = useMemo(() => {
    if (!metricsKpis) {
      return null;
    }
    return [
      {
        id: 'none',
        label: formatMessage({
          defaultMessage: 'None',
          id: 'reporting.disaggregatedSelector.selectorOptions.none'
        })
      },
      {
        id: 'metric',
        label: metricsKpis.metric.label
      },
      ...(!hideKpis
        ? [
            {
              id: 'kpi',
              label: metricsKpis.kpi.label
            },
            {
              id: 'both',
              label: `${metricsKpis.metric.label} & ${metricsKpis.kpi.label}`
            }
          ]
        : [])
    ];
  }, [metricsKpis]);

  const disaggregatedByOptionsExtraFilter = (campaigns, variable) => {
    return (
      {
        'contextualBidding.scenarioId': () => campaignsHaveContextualBidding(campaigns),
        'dco.contextId': () => campaignsHaveDCO(campaigns),
        'dco.scenarioId': () => campaignsHaveDCO(campaigns)
      }[variable]?.() ?? true
    );
  };

  const selectorDisaggregatedByOptions = useMemo(() => {
    if (metric === 'none') {
      return [];
    }
    return [
      ...(reportingCampaignsIds.length > 1 ? ['cid'] : []),
      ...chartDisaggregatedVariables.filter(variable =>
        checkUserResourcePermissions(getVariableFilterPermission(variable), user, userRoles)
      )
    ]
      .filter(variable => disaggregatedByOptionsExtraFilter(selectedCampaign, variable))
      .map(variable => ({
        id: variable,
        label: formatMessage({
          defaultMessage: variable,
          id: `reporting.disaggregatedSelector.selectorOptions.disaggregatedOption${variableNameFormatter(variable)}`
        })
      }));
  }, [metric, metricsKpis, reportingCampaignsIds]);

  const onChangeDisaggregateSelector = useCallback(
    option => {
      setState(prevState => ({
        ...prevState,
        metric: option.id,
        ...(option.id === 'none' && { by: null })
      }));
      onChangeDisaggregatedOption(option.id, option.id === 'none' ? null : by);
    },
    [by]
  );

  const onChangeDisaggregateBySelector = useCallback(
    option => {
      setState(prevState => ({
        ...prevState,
        by: option.id
      }));
      onChangeDisaggregatedOption(metric, option.id);
    },
    [metric]
  );

  return (
    <div className="reportingDisaggregated">
      <label className="subtitleDefault fontRegular">
        {formatMessage({
          defaultMessage: 'Disaggregated',
          id: 'reporting.disaggregatedSelector.selectorLabel'
        })}
      </label>
      <Selector
        className="reportingDisaggregated__selector"
        defaultValue={[selectorOptions[0]]}
        onChange={onChangeDisaggregateSelector}
        options={selectorOptions}
        {...(metric === 'none' && { value: [selectorOptions[0]] })}
        type="simple"
      />
      <label className="subtitleDefault fontRegular">
        {formatMessage({
          defaultMessage: 'by',
          id: 'reporting.disaggregatedSelector.selectorLabel'
        })}
      </label>
      <Selector
        className="reportingDisaggregated__selector"
        isDisabled={isDisabledDisaggregatedBySelector}
        onChange={onChangeDisaggregateBySelector}
        options={selectorDisaggregatedByOptions}
        {...(!by && { value: [by] })}
        type="simple"
      />
    </div>
  );
}

ReportingDisaggregatedSelectorMultiLine.propTypes = {
  hideKpis: PropTypes.bool.isRequired,
  metricsKpis: PropTypes.object.isRequired,
  onChangeDisaggregatedOption: PropTypes.func.isRequired
};

export default React.memo(ReportingDisaggregatedSelectorMultiLine);
